import { AlertTriangle } from 'lucide-react';

// Long-form page pieces shared by the legal pages and the setup guide: a
// reading-width column, its title block, numbered/anchored sections and
// the callout card.

export function Article({ children, className = '' }) {
  return <article className={`px-4 md:px-8 pb-24 max-w-3xl mx-auto ${className}`}>{children}</article>;
}

// `subtitle` is the small italic line under the title (the legal pages'
// "Last updated" date); `lede` is a full-size intro sentence.
export function ArticleHeader({ title, subtitle, lede, children }) {
  return (
    <header className="pt-6 pb-10 border-b border-[var(--color-divider)]">
      <h1 className="text-4xl md:text-5xl font-bold font-serif tracking-tight mb-3">{title}</h1>
      {subtitle && <p className="text-sm text-[var(--color-ink-light)] font-serif italic">{subtitle}</p>}
      {lede && <p className="text-lg text-[var(--color-ink-light)] leading-relaxed max-w-2xl">{lede}</p>}
      {children}
    </header>
  );
}

// A titled section. With an `id` it is a link target; scroll-mt keeps the
// heading clear of the top edge when jumped to.
export function Section({ id, title, children, className = '' }) {
  return (
    <section id={id} className={`space-y-4 scroll-mt-6 ${className}`}>
      <h2 className="font-serif font-bold text-2xl text-[var(--color-ink-main)]">{title}</h2>
      <div className="space-y-4 text-[var(--color-ink-main)]">{children}</div>
    </section>
  );
}

const TONES = {
  note: {
    box: 'bg-[var(--color-paper-warm)] border-[var(--color-divider)]',
    Icon: null,
    iconClass: '',
  },
  warning: {
    box: 'bg-[var(--color-surface-elevated)] border-[var(--color-accent-gold)]',
    Icon: AlertTriangle,
    iconClass: 'text-[var(--color-accent-warm)]',
  },
};

// The paper card used for "At a glance" summaries (tone "note") and for
// things that will bite if skipped (tone "warning"). `as` picks the title's
// heading level so a callout nested inside a Section keeps the outline.
export function Callout({ title, tone = 'note', as: Heading = 'h2', children, className = '' }) {
  const t = TONES[tone] || TONES.note;
  const Icon = t.Icon;
  return (
    <aside role="note" className={`p-6 border rounded-2xl ${t.box} ${className}`}>
      {title && (
        <Heading className="font-serif font-bold text-lg mb-3 flex items-center gap-2">
          {Icon && <Icon className={`w-5 h-5 flex-shrink-0 ${t.iconClass}`} aria-hidden="true" />}
          {title}
        </Heading>
      )}
      <div className="text-[var(--color-ink-main)] leading-relaxed space-y-3">{children}</div>
    </aside>
  );
}
